// @ts-nocheck -- Legacy JavaScript compatibility boundary; runtime behavior intentionally preserved.
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import MobileStepper from "@mui/material/MobileStepper";
import Box from "@mui/material/Box";
import { useMediaQuery, useTheme } from "@mui/material";

import { Grid } from "../grid";
import Slide from "./slide";

/**
 * @param {{
 * sx: import("@mui/material").SxProps<import("@mui/material").Theme>
 * children: Array<Slide> | Slide
 * duration: Number
 * }}
 */
export function SlideShow({ sx, children, duration = 5 }: any) {
  const [slides, setSlides] = useState([...children]);

  const [[page, direction], setPage] = useState([0, 1]);
  const [isHovering, setIsHovering] = useState(false);

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    setSlides([...children]);
  }, [children]);

  const { length } = slides;

  const pageIndex = page % length;

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (length > 1 && !isHovering) {
      const interval = setInterval(() => {
        setPage([page + 1, direction]);
      }, duration * 1000);

      return () => clearInterval(interval);
    }
  }, [length, duration, isHovering, direction, page]);

  const paginate = (newDirection: any) => {
    const newPage = newDirection > 0 ? page + newDirection : Math.abs(length + page + newDirection);

    setPage([newPage, newDirection]);
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        ...sx,
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <AnimatePresence initial={false} custom={direction}>
        <Grid
          justifyContent="space-between"
          spacing={isMobile ? 0 : 2}
          sx={{ "& .MuiGrid-item": { paddingX: 0 } }}
        >
          <Grid.Item xs={12}>
            <motion.div
              key={page}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.5 },
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e: any, { offset, velocity }: any) => {
                const swipe = swipePower(offset.x, velocity.x);

                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
            >
              {slides[pageIndex]}
            </motion.div>
          </Grid.Item>
          <Grid.Item xs={12}>
            <Box sx={{ width: "100%", display: "flex" }}>
              <MobileStepper
                steps={length}
                activeStep={pageIndex}
                variant="progress"
                position="static"
                sx={{ backgroundColor: "transparent", margin: "auto" }}
              />
            </Box>
          </Grid.Item>
        </Grid>
      </AnimatePresence>
    </div>
  );
}

const variants = {
  enter: (direction: any) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: any) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
  }),
};

const swipeConfidenceThreshold = 10000;

const swipePower = (offset: any, velocity: any) => Math.abs(offset) * velocity;

SlideShow.Slide = Slide;

SlideShow.propTypes = {
  children: PropTypes.node.isRequired,
  duration: PropTypes.number,
  sx: PropTypes.object,
};
